import React, { useState } from 'react';
import { Button, Col, Collapse, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Block from '../../../components/Block';
import Icon from '../../../components/Icon';
import Title from '../../../components/Title';
import ShowItem from '../../../components/ShowItem';
import { useDestroyPublisherInvoiceMutation, useFinalizePublisherInvoiceMutation, useGetPublisherInvoiceQuery } from '../../../api/publisherInvoiceApiSlice';
import { useHistory, useParams } from 'react-router-dom';
import { Loader } from '../../../components/components';
import {formatDate, formatNumber, formatPercent, formatPrice} from '../../../components/Format';
import styled from "styled-components";
import format from 'date-fns/format';
import enGB from 'date-fns/locale/en-GB'
import fr from 'date-fns/locale/fr'
import enUS from 'date-fns/locale/en-US'
import { useGetPublisherQuery } from '../../../api/publisherApiSlice';
import PublisherEditInvoiceModal from '../../../components/publisher/admin/invoice/PublisherInvoiceEditModal';
import {useHistoryState, usePropState} from "../../../components/utils";
const locales = {'en-GB': enGB, 'fr-FR': fr, 'en-US': enUS}

const Composite = ({data}) => {
  const { t, i18n } = useTranslation();

  return <>
    { data.composite.map((elt, i)=> {
      return (
        <React.Fragment key={i}>
          <ShowItem label={`${formatNumber(elt.from)} - ${formatNumber(elt.to)}`} value={formatPrice(elt.amount, {digits: 1})}>
            <ShowItem label={t('publisherInvoice.mau')} value={formatNumber(elt.to - elt.from)}/>
            <ShowItem label={t(`publisherInvoice.${elt.model}`)} value={formatPrice(elt.value, {digits: 1})}/>
          </ShowItem>
        </React.Fragment>
      )
    }) }
  </>
}

const MinGuarantedIncomePartial = ({data}) => {
  const { t, i18n } = useTranslation();
  if (data.mrg_model == "cpm") {
    return (<ShowItem label={t("publisherInvoice.cpm")} value={formatPrice(data.mrg_pricing.cpm, {digits: 1})}/>)
  } else if (data.mrg_model == "fixed") {
    return (<ShowItem label={t("publisherInvoice.fixed")} value={formatPrice(data.mrg_value, {digits: 1})}/>)
  } else if (data.mrg_model == "composite") {
    return <Composite data={data} />
  } else {
    return (<></>)
  }
}

const AppCollapsableTitle = styled.div`
  cursor: pointer;
  padding: 0 15px;
  margin-top: 15px;
`

const AppCollapsableTitleIcon = styled("i")`
  transition: transform .35s ease;
  transform: ${props => props.active ? "rotate(90deg)" : ""};
`

const ApplicationDetails = ({data = {}}) => {
  const [appShow, setAppShow] = useState(false)
  const { t, i18n } = useTranslation();

  return (<>
    <AppCollapsableTitle
      onClick={(e) => {
        e.stopPropagation()
        setAppShow(!appShow)
      }}
      aria-controls="example-collapse-text"
      aria-expanded={appShow}>
        {t("publisherInvoice.details")} <AppCollapsableTitleIcon active={appShow} className="fa-solid fa-caret-right"/>
    </AppCollapsableTitle>
    <Collapse in={appShow}>
      <div id="example-collapse-text">
      {
        Object.keys(data).map((os) => (
          <React.Fragment key={os}>
            <ShowItem label={t(`publisherInvoice.${os}`)} value={formatPrice(Object.values(data[os]).reduce((acc, val)=> (acc + val[["amount"]]), 0), {digits: 2})}>
              { Object.keys(data[os]).map((k,i) => (
                 <ShowItem label={k} key={`${os}_${i}`} value={formatPrice(data[os][k]["amount"], {digits: 2})}/>
              ))
              }
            </ShowItem>
          </React.Fragment>)
        )
      }
      </div>
    </Collapse>
  </>)
}

const PublisherInvoice = (props) => {
  const { t, i18n } = useTranslation();
  const {publisherId, year, month} = useParams();
  const history = useHistory();
  const {data: publisher = {}} = useGetPublisherQuery({publisherId})
  const {data={}, isSuccess, isLoading} = useGetPublisherInvoiceQuery([publisherId, year, month]);
  const subTitle = t('invoice') + ' - ' + format(new Date(parseInt(year), (parseInt(month)-1), 1), 'MMMM yyyy', {locale: locales[i18n.language]})
  const onBackPath = useHistoryState(props, "onBackPath")
  const onBackTab = useHistoryState(props, "onBackTab")

  const onBack = () => {
    let pathParams = {
      pathname: onBackPath ? onBackPath : `/publishers/${publisherId}/revenues`
    }
    if (onBackTab) {
      pathParams = {...pathParams, state: {activeTab: onBackTab}}
    }
    history.push(pathParams)
  }

  const [showEdit, setShowEdit] = useState(false)
  const [finalize] = useFinalizePublisherInvoiceMutation()
  const [destroy] = useDestroyPublisherInvoiceMutation()

  const unFinalizedButtons = [
    <Button onClick={()=>setShowEdit(true)}>
      <Icon icon='edit' text={t("edit")} />
    </Button>,
    <Button onClick={()=>finalize([publisherId, year, month])}>
      <Icon icon='check-square' text={t("publisherInvoice.finalize")} />
    </Button>,
    <Button onClick={()=>destroy([publisherId, year, month]).then(onBack())}>
      <Icon icon='trash' text={t("delete")} />
    </Button>
  ]

  const downloadPdf = () => {
    const csrfToken = document.querySelector('[name=csrf-token]');
    const url = (`/v1/publishers/${publisherId}/revenues/${year}/${month}/download_pdf`)
    const options = {
      headers: {
        'X-CSRF-TOKEN': csrfToken.content
      }
    };
    let filename = 'file.pdf';
    fetch(url, options)
      .then( res => {
        const header = res.headers.get('Content-Disposition');
        const parts = header?.split(';');
        filename = parts[1];
        return res.blob();
      })
      .then(blob => {
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    });
  }

  const finalizedButtons = [
    <Button onClick={()=>(downloadPdf())}>
      <Icon icon='pdf' text={t('publisherInvoice.invoicingRequest')} />
    </Button>
  ]

  return ( <>
    <Title
      main={publisher.name}
      sub={subTitle}
      onBack={onBack}
      children={data.final ? finalizedButtons : unFinalizedButtons}
      />
    <PublisherEditInvoiceModal show={showEdit} setShow={setShowEdit} data={data}/>
    <Block title={`${t('publisherInvoice.from')} ${formatDate(data.start_date, {i18n})} ${t('publisherInvoice.to')} ${formatDate(data.end_date, {i18n})}`}>
      <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess}>
        <ShowItem label={t('publisherInvoice.mau')} value={formatNumber(data.billed_users)}/>
        <MinGuarantedIncomePartial data={data}/>
        {data.prorata_temporis != 1 && <ShowItem label={t("publisherInvoice.prorata")} value={formatNumber(data.prorata_temporis, {digits: 2})}/>}
        <ShowItem label={t("publisherInvoice.mrg")} value={formatPrice(data.mrg, {digits: 2})}>
          {(data.mrg_model !== 'cpm') && <ShowItem label={t("publisherInvoice.cpm")} value={formatPrice(data.blended_mrg_cpm, {digits: 1})}/>}
        </ShowItem>
        <ShowItem label={t("publisherInvoice.revshare_basis")} value={formatPrice(data.rev_share_basis, {digits: 2})}/>
        <ShowItem label={t("publisherInvoice.revshare")} value={formatPercent(data.rev_share*100, {digits: 0})}/>
        <ShowItem label={t("publisherInvoice.revshare_amount")} value={formatPrice(data.rev_share_amount, {digits: 2})}/>
        <ShowItem label={t("publisherInvoice.price")} value={formatPrice(data.price, {digits: 2})}>
          <ShowItem label={t("publisherInvoice.cpm")} value={formatPrice(data.blended_cpm, {digits: 1})}/>
        </ShowItem>
        <ApplicationDetails data={data.split_by_app}/>
      </Loader>
    </Block>
  </> );
}

export default PublisherInvoice;
